import { LoadClassification, MeasureId, RetrieveSlotListsRequestDto, Slot, SlotList } from '../../../model/CORE';
import React, { PureComponent } from 'react';
import { ServiceFactory } from '../../../service/ServiceFactory';
import MeasureUtils from '../../../common/util/MeasureUtils';
import { Flex } from '../../common/Flex';
import { Button, Col, Row } from 'antd';
import TimeUtils from '../../../common/util/TimeUtils';
import Utils from '../../../common/util/Utils';
import AppContext from 'app/common/context/AppContext';

interface SlotListSynthesisForMeasureProps {
    measureId: MeasureId;
    slotListButtonVisible?: boolean;
    onSlotListClick?: () => void;
}

interface SlotListSynthesisForMeasureState {
    slotList: SlotList;
}

export class SlotListSynthesisForMeasure extends PureComponent<
    SlotListSynthesisForMeasureProps,
    SlotListSynthesisForMeasureState
> {
    componentDidMount(): void {
        this.refresh();
    }
    componentDidUpdate(prevProps) {
        if (this.props?.measureId !== prevProps?.measureId) {
            this.refresh();
        }
    }
    refresh = () => {
        if (!this.props.measureId) return;

        const req: RetrieveSlotListsRequestDto = {
            dataset: AppContext.getDataset(),
            regulationIds: [this.props.measureId],
        };
        ServiceFactory.coreService()
            .retrieveSlotList(req)
            .then(reply => {
                if (reply?.data?.length === 1) {
                    const slotList = reply?.data[0]; // Expecting only 1 slot list
                    this.setState({ slotList: slotList });
                } else {
                    this.setState({ slotList: null });
                }
            })
            .catch(error => this.setState({ slotList: null }));
    };
    render() {
        return (
            <Flex direction="vertical">
                <Flex>Slot list synthesis</Flex>
                <SlotListSynthesis
                    slotList={this.state?.slotList}
                    slotListButtonVisible={this.props.slotListButtonVisible}
                    onSlotListClick={this.props.onSlotListClick}
                />
            </Flex>
        );
    }
}

interface SlotListSynthesisProps {
    slotList: SlotList;
    slotListButtonVisible?: boolean;
    onSlotListClick?: () => void;
}

export class SlotListSynthesis extends PureComponent<SlotListSynthesisProps> {
    /* Return all slots from slot sequences to a single array */
    getSlots = (): Slot[] => {
        return this.props?.slotList?.slotSequences?.map(slotSequence => slotSequence.slots).flat();
    };
    renderSlotListButton = () => {
        if (!this.props.slotListButtonVisible) return;

        return (
            <Flex size={'10%'} style={{ alignSelf: 'center' }}>
                <Button onClick={() => this.props.onSlotListClick?.()}>Slot List</Button>
            </Flex>
        );
    };
    render() {
        if (!this.getSlots()) return null;

        const slots = this.getSlots();
        const mostPenalisingSlots = slots.filter(slot => slot.regulatedFlight?.mostPenalising);
        const nbSlots = slots.length;
        const nbFlightsCaught = mostPenalisingSlots.filter(slot => Utils.isExists(slot.regulatedFlight)).length;
        const nbFlightsDelayed = mostPenalisingSlots.filter(slot => slot.regulatedFlight?.delayInMs > 0).length;
        const nbMinutesDelay = TimeUtils.millescondsToMinutes(
            mostPenalisingSlots.reduce(
                (total, slot) => total + (slot.regulatedFlight?.delayInMs >= 0 && slot.regulatedFlight?.delayInMs) || 0,
                0,
            ),
        );
        const averageDelay = nbFlightsCaught > 0 ? Math.round(nbMinutesDelay / nbFlightsCaught) : 0;
        const maxDelay =
            mostPenalisingSlots.length > 0
                ? TimeUtils.millescondsToMinutes(
                      Math.max(...mostPenalisingSlots.map(slot => slot.regulatedFlight?.delayInMs || 0)),
                  )
                : 0;
        const delays = mostPenalisingSlots
            .filter(slot => slot.regulatedFlight?.delayInMs > 0)
            .map(slot => slot.regulatedFlight?.delayInMs);

        return (
            <Flex>
                <Flex size={'50%'}>
                    <Row style={{ width: '100%' }}>
                        <Col span={4} className="centered">
                            {nbFlightsCaught}
                            <br />
                            <span style={{ fontSize: '0.9em' }}>flights caught</span>
                        </Col>
                        <Col span={4} className="centered">
                            {nbFlightsDelayed}
                            <br />
                            <span style={{ fontSize: '0.9em' }}>flights delayed</span>
                        </Col>
                        <Col span={4} className="centered">
                            {nbMinutesDelay}
                            <br />
                            <span style={{ fontSize: '0.9em' }}>total delay (min)</span>
                        </Col>
                        <Col span={4} className="centered">
                            {averageDelay}
                            <br />
                            <span style={{ fontSize: '0.9em' }}>avg delay (min)</span>
                        </Col>
                        <Col span={4} className="centered">
                            {maxDelay}
                            <br />
                            <span style={{ fontSize: '0.9em' }}>max delay (min)</span>
                        </Col>
                        <Col span={4} className="centered">
                            {Math.floor(100 * (1 - nbFlightsCaught / nbSlots))}%<br />
                            <span style={{ fontSize: '0.9em' }}>free slots</span>
                        </Col>
                    </Row>
                </Flex>
                <Flex className="centered" style={{ padding: '0 2em' }}>
                    {[
                        LoadClassification.NORMAL,
                        LoadClassification.LOW_THRESHOLD,
                        LoadClassification.HIGH_THRESHOLD,
                        LoadClassification.OVERLOAD,
                    ].map(lc => {
                        const nbFlights = delays.filter(
                            delay => lc === MeasureUtils.toLoadClassification(delay),
                        ).length;
                        if (nbFlights === 0) return undefined;

                        const pct = Math.floor(100 * (nbFlights / nbFlightsDelayed)) + '%';
                        return (
                            <Flex key={lc} size={pct} direction="vertical">
                                <Flex className="centered">{nbFlights}</Flex>
                                <Flex style={{ backgroundColor: MeasureUtils.getFillColor(lc), height: '1em' }}></Flex>
                                <Flex className="centered" style={{ fontSize: '0.9em' }}>
                                    {pct}
                                </Flex>
                            </Flex>
                        );
                    })}
                </Flex>
                {this.renderSlotListButton()}
            </Flex>
        );
    }
}
